import {
  hasResolvedDocumentValue,
  useDocument,
} from "@shared/components/providers/SupergrainProvider"
import { cx } from "@shared/components/untitledui/util/cx"
import { followedCourseUnavailableReason } from "@shared/lib/followed-course"
import type { ParentCardStack, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course, CourseCalendarTime } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import type Session from "cc-frontend/services/session"
import type { ReactNode, Ref } from "react"
import { useEmberService } from "react-app/providers/ember-service-context"
import ChangeTime from "./ChangeTime"
import FollowedCourseBar, { FollowedCourseNoAccessBar } from "./FollowedCourseBar"
import { reportFollowedLoadError } from "./followed-course-errors"
import { useFollowedCoursePlanbook } from "./use-followed-course-planbook"

interface Props {
  course: Course
  courseDate: CourseDateCombined
  planbook: Planbook
  rotationCalendar: RotationCalendar
  /** Persists the lesson's custom time; `null` clears it back to the course default. */
  setCustomTime: (time: CourseCalendarTime | null) => void
  /** Whether anything sits under the bar — the note or the lesson — squaring its bottom corners. */
  hasBodyBelow: boolean
  menu: ReactNode
  onCopyFollowedLesson?: (followedLesson: UnifiedCardStack, followedDateString: string) => void
  /** The lesson this day's lesson was copied from, which may be one of the followed class's. */
  parentCardStack: ParentCardStack | null
  /** The planner is showing this lesson collapsed to its title. */
  isCollapsed?: boolean
  editCourseTitleAndOrder: (courseId: string) => void
  ref?: Ref<HTMLDivElement>
  className?: string
}

/**
 * The COURSE's title, not the lesson's — the lesson's own title lives in the editor below.
 */
export default tracked(function CourseTitleBar({
  course,
  courseDate,
  planbook,
  rotationCalendar,
  setCustomTime,
  hasBodyBelow,
  menu,
  onCopyFollowedLesson,
  parentCardStack,
  isCollapsed = false,
  editCourseTitleAndOrder,
  ref,
  className,
}: Props) {
  const session = useEmberService<Session>("session")
  const hasFollowClassesFeature =
    session.user?.content?.attributes.featureFlags.FOLLOW_CLASSES ?? false
  // "Just notes" classes don't follow one; this only catches follows saved before that rule.
  const followedCourseId =
    !hasFollowClassesFeature || course.attributes.isNonInstructional
      ? null
      : (course.attributes.followedCourse?.courseId ?? null)
  const followedCourseHandle = useDocument("course", followedCourseId)
  if (followedCourseHandle.status === "error") reportFollowedLoadError(followedCourseHandle.error)
  const followedCourse = hasResolvedDocumentValue(followedCourseHandle)
    ? followedCourseHandle.value
    : null
  const followed = useFollowedCoursePlanbook(followedCourse)
  const unavailableReason =
    followedCourse !== null && followed.planbook !== null && !followed.hasError
      ? followedCourseUnavailableReason(followedCourse, followed.planbook)
      : undefined
  const hasFollowedBar = unavailableReason === null || unavailableReason === "no-access"

  return (
    <>
      {unavailableReason === null && (
        <FollowedCourseBar
          course={course}
          followedCourse={followedCourse!}
          followedPlanbook={followed.planbook!}
          followedRotationCalendar={followed.rotationCalendar}
          planbook={planbook}
          rotationCalendar={rotationCalendar}
          dateString={courseDate.dateString}
          isClassOff={courseDate.isOff}
          isCollapsed={isCollapsed}
          onCopyFollowedLesson={onCopyFollowedLesson}
          parentCardStack={parentCardStack}
          editCourseTitleAndOrder={editCourseTitleAndOrder}
          className="rounded-t-md"
        />
      )}
      {unavailableReason === "no-access" && (
        <FollowedCourseNoAccessBar
          course={course}
          followedCourse={followedCourse!}
          className="rounded-t-md"
        />
      )}
      <div
        ref={ref}
        className={cx(
          "set-course-color-vars take-course-background-color @container",
          // cursor-default: the header is a label, not a drag pad (the grip is) — an
          // I-beam over it reads as "text", inviting selection instead of the grip.
          "flex cursor-default items-center justify-between gap-2 px-2.5 py-2.25 text-[0.9375rem] leading-none",
          // Rounded on every day, off ones included: the top of the card reads as clipped
          // otherwise. A followed course bar above this one is the top instead, and rounds for it.
          !hasFollowedBar && "rounded-t-md",
          !hasBodyBelow && "rounded-b-md",
          hasBodyBelow && "border-b border-black/10",
          courseDate.isOff &&
            "[background-image:repeating-linear-gradient(-45deg,rgba(0,0,0,0.04)_0,rgba(0,0,0,0.04)_12px,rgba(0,0,0,0.1)_12px,rgba(0,0,0,0.1)_24px)]",
          className,
        )}
        data-color={course.attributes.color}
        data-test="course title bar"
      >
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
          {courseDate.isOff && (
            <span
              data-test="no-class-banner"
              className="text-xs font-bold tracking-wider text-slate-900 uppercase select-none"
            >
              NO CLASS
            </span>
          )}
          <span data-test="course title" className="font-bold [overflow-wrap:anywhere] select-none">
            {course.attributes.title}
          </span>
          <ChangeTime courseDate={courseDate} course={course} setCustomTime={setCustomTime} />
        </div>
        <div className="flex shrink-0 items-center gap-1.5">{menu}</div>
      </div>
    </>
  )
})
